import {
  Configuration as OrderingConfiguration,
  OrderApi,
  OrderedJobsApi,
  OrderedProjectApi,
  OrderedProjectHistoryApi,
  SummedOrderedJobsApi,
  SummedOrderedProjectsApi,
} from '@its4plan/ordering';
import {
  SinglePriolistPdfApi,
  FullPriolistPdfApi,
  ChecklistApi,
  CompanySummaryApi,
  Configuration as PdfConfiguration,
} from '@its4plan/pdf_service';
import { useAuthenticationService } from '~/common/services/AuthenticationService';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { AxiosResponse } from 'axios';

export type Timestamp = number;
export type ID = number | string;
export type IdProvider =
  | {
      id: ID | null | undefined;
    }
  | null
  | undefined;

export const unwrap = <T,>(res: AxiosResponse<T>) => res.data;
export type RequestOptions = {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
};

function configFactory(authToken: string | undefined, baseUrl: string) {
  return {
    basePath: baseUrl,
    headers: { Authorization: authToken ? `Bearer ${authToken}` : '' },
  };
}

export function baseUrlFactory(prefix: string, fallback: string) {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') {
    return fallback;
  }
  const [_, ...rest] = hostname.split('.');
  return 'https://' + prefix + '.' + rest.join('.');
}

function createConfigurations(authToken: string | undefined) {
  return {
    orderingConfig: new OrderingConfiguration(
      configFactory(
        authToken,
        baseUrlFactory(
          'orders',
          import.meta.env.VITE_APP_ORDERING_URL as string,
        ),
      ),
    ),
    pdfConfig: new PdfConfiguration(
      configFactory(
        authToken,
        baseUrlFactory(
          'pdf',
          import.meta.env.VITE_APP_PDF_SERVICE_URL as string,
        ),
      ),
    ),
  };
}

function registerApis(authToken: string | undefined) {
  const { orderingConfig, pdfConfig } = createConfigurations(authToken);
  return {
    orderApi: new OrderApi(orderingConfig),
    orderedProjectApi: new OrderedProjectApi(orderingConfig),
    orderedJobApi: new OrderedJobsApi(orderingConfig),
    summedOrderedProjectApi: new SummedOrderedProjectsApi(orderingConfig),
    summedOrderedJobsApi: new SummedOrderedJobsApi(orderingConfig),
    orderedProjectHistoryApi: new OrderedProjectHistoryApi(orderingConfig),
    singlePriolistPdfApi: new SinglePriolistPdfApi(pdfConfig),
    fullPriolistPdfApi: new FullPriolistPdfApi(pdfConfig),
    checklistApi: new ChecklistApi(pdfConfig),
    companySummaryApi: new CompanySummaryApi(pdfConfig),
  };
}

type TApiContext = ReturnType<typeof registerApis>;

const ApiContext = createContext<TApiContext>(null as unknown as TApiContext);

export function useApiContext() {
  const apiContext = useContext(ApiContext);
  if (apiContext === null) {
    throw new Error('No Api Context found!');
  }
  return apiContext;
}

export function ApiContextProvider(props: { children: ReactNode }) {
  const { authenticationState } = useAuthenticationService();
  const apis = useMemo(
    () => registerApis(authenticationState?.token),
    [authenticationState?.token],
  );

  return (
    <ApiContext.Provider value={apis}>{props.children}</ApiContext.Provider>
  );
}
